/**
 * Angular Alert Component
 *
 * This is a migrated version of the AngularJS acme-alert component.
 * It demonstrates the migration pattern for components.
 *
 * Original: ui/src/app/common/components/alert/alert.component.ts
 *
 * Usage in Angular templates:
 *   <app-alert type="warning">Warning message</app-alert>
 *   <app-alert type="info" icon="bell">Custom icon</app-alert>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-alert type="warning">Warning message</ng-alert>
 */
import { Component, Input, HostBinding, OnChanges } from '@angular/core';

export type AlertType = 'default' | 'info' | 'warning' | 'error' | 'success';

@Component({
  selector: 'app-alert',
  standalone: false,
  template: `
    <div *ngIf="iconClass" class="acme-alert__prefix">
      <span class="acme-alert__icon" [ngClass]="iconClass"></span>
    </div>
    <div class="acme-alert__content">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        min-height: 36px;
        border-radius: 4px;
      }

      :host(.acme-alert--default) {
        border: 1px solid #767676;
      }
      :host(.acme-alert--default) .acme-alert__prefix {
        background-color: #767676;
      }
      :host(.acme-alert--default) .acme-alert__icon {
        color: #ffffff;
      }

      :host(.acme-alert--info) {
        border: 1px solid #808080;
      }
      :host(.acme-alert--info) .acme-alert__prefix {
        background-color: #808080;
      }
      :host(.acme-alert--info) .acme-alert__icon {
        color: #ffffff;
      }

      :host(.acme-alert--warning) {
        border: 1px solid #f9b31b;
      }
      :host(.acme-alert--warning) .acme-alert__prefix {
        background-color: #f9b31b;
      }
      :host(.acme-alert--warning) .acme-alert__icon {
        color: #333333;
      }

      :host(.acme-alert--error) {
        border: 1px solid #e5291b;
      }
      :host(.acme-alert--error) .acme-alert__prefix {
        background-color: #e5291b;
      }
      :host(.acme-alert--error) .acme-alert__icon {
        color: #ffffff;
      }

      :host(.acme-alert--success) {
        border: 1px solid #2c7b7c;
      }
      :host(.acme-alert--success) .acme-alert__prefix {
        background-color: #2c7b7c;
      }
      :host(.acme-alert--success) .acme-alert__icon {
        color: #ffffff;
      }

      .acme-alert__prefix {
        min-height: 100%;
        width: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .acme-alert__content {
        flex: 1;
        padding: 8px 10px;
        display: flex;
        align-items: center;
        line-height: 1rem;
      }
    `,
  ],
})
export class AlertComponent implements OnChanges {
  private static readonly ICONS: Record<string, string> = {
    info: 'fa fa-lg fa-info-circle',
    warning: 'fa fa-lg fa-exclamation-circle',
    error: 'fa fa-lg fa-exclamation-circle',
    success: 'fa fa-lg fa-check-circle',
  };

  /** Alert type - determines color scheme */
  @Input() type: AlertType = 'default';

  /** Optional custom icon name (FontAwesome) */
  @Input() icon?: string;

  /** Computed icon class */
  iconClass: string | null = null;

  @HostBinding('class')
  get hostClass(): string {
    return `acme-alert acme-alert--${this.type}`;
  }

  ngOnChanges(): void {
    if (this.icon) {
      this.iconClass = `fa fa-lg fa-${this.icon}`;
    } else {
      this.iconClass = AlertComponent.ICONS[this.type] || null;
    }
  }
}

